/* eslint-disable @typescript-eslint/no-explicit-any */
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Checkbox, IconButton, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { baseCell, currencyCell, dateCell, booleanCell } from 'components/cells';
import {
  numberFilterOperators,
  filterOperators,
  dateFilterOperators,
  booleanFilterOperators
} from 'components/enhanced';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import TransportDocumentLineUpdateDialog from 'features/inventory/transport-documents/TransportDocument/components/TransportDocumentLineUpdateDialog';
import { useTranslation } from 'react-i18next';

export function TransportDocumentLinesColumns(
  locked = false,
  headerInfo = true,
  onCloseUpdate?: () => void,
  handleCheck?: (id: any) => void,
  handleUncheck?: (id: any) => void,
  handleUpdateExcluded?: (id: any, excluded: boolean) => void,
  disableEdit?: boolean
): GridColDef[] {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'checkbox',
      headerName: t('actions.print'),
      width: 50,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const id = params.id;

        return (
          <Checkbox
            disabled={locked}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={(event) => {
              event.stopPropagation();

              if (event.target.checked) {
                handleCheck?.(id);
              } else {
                handleUncheck?.(id);
              }
            }}
          />
        );
      }
    },
    {
      field: 'quantity',
      headerName: t(`global.quantity`),
      width: 80,
      type: 'number',
      filterOperators: numberFilterOperators,
      renderCell: baseCell
    }
  ];

  if (!handleUpdateExcluded) {
    columns.push(
      // 17/12/2024 : Divisione fra DDT e Fattura
      // Richiesta da Stefano l'eliminazione della colonna "importo fatturato"
      {
        field: 'amount',
        headerName: t(`entities.ddt.amount`),
        type: 'number',
        filterable: true,
        filterOperators: numberFilterOperators,
        sortable: true,
        width: 125,
        editable: !locked,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value);
        },
        renderCell: currencyCell
      }
    );
  }

  columns.push(
    {
      field: 'expected_amount',
      headerName: t(`entities.ddt.expected_amount`),
      filterOperators: numberFilterOperators,
      type: 'number',
      width: 125,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value);
      },
      renderCell: currencyCell
    },
    {
      field: 'document',
      headerName: t(`entities.ddt.ref`),
      width: 100,
      filterable: true,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      // richiesta di Stefano per ROMA 2 : 18/12/2024
      field: 'notes',
      headerName: t(`entities.ddt.notes`),
      filterOperators,
      editable: !disableEdit,
      width: 200,
      renderCell: baseCell
    }
  );

  columns.push(
    ...[
      {
        field: 'product',
        headerName: t(`global.product`),
        width: 150,
        renderCell: baseCell
      },
      {
        field: 'description',
        headerName: t(`global.description`),
        width: 250,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'contract',
        headerName: t(`global.contract`),
        filterOperators,
        width: 75,
        renderCell: baseCell
      },
      {
        field: 'supplier',
        headerName: t(`global.supplier`),
        width: 150,
        filterable: false,
        sortable: false,
        renderCell: baseCell
      },
      {
        field: 'warehouse',
        headerName: t(`global.warehouse`),
        width: 150,
        filterable: true,
        filterOperators,
        sortable: true,
        renderCell: baseCell
      },
      {
        field: 'product_batch',
        headerName: t(`entities.movement.product_batch`),
        width: 100,
        filterable: false,
        renderCell: baseCell
      },
      {
        field: 'expiration_date',
        headerName: t(`entities.movement.expiration_date`),
        width: 100,
        filterable: false,
        renderCell: dateCell
      },
      {
        field: 'date',
        headerName: t(`entities.ddt.movement_date`),
        filterOperators: dateFilterOperators,
        width: 110,
        renderCell: dateCell
      },
      {
        field: 'discounted',
        width: 50,
        headerName: t(`entities.movement.discounted`),
        filterOperators: booleanFilterOperators,
        renderCell: booleanCell(t)
      },
      {
        field: 'unit_price',
        headerName: t(`entities.movement.unit_price`),
        type: 'number',
        filterable: true,
        filterOperators: numberFilterOperators,
        sortable: true,
        width: 150,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value);
        },
        renderCell: currencyCell
      },
      {
        field: 'order_code',
        headerName: t(`entities.movement.order_code`),
        filterOperators,
        width: 150,
        renderCell: baseCell
      }
    ]
  );

  if (handleUpdateExcluded && !disableEdit) {
    columns.push(
      // ROMA changes
      {
        field: 'excluded_movement',
        headerName: t('global.already_counted'),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => [
          <Tooltip
            key={1}
            placement="top"
            title={
              params.row.excluded_movement
                ? t('global.excluded_movement')
                : t('global.included_movement')
            } // Forces the tooltip to appear above the object
          >
            <IconButton
              onClick={() => handleUpdateExcluded(params.row.id, !params.row.excluded_movement)}>
              {params.row.excluded_movement ? (
                <RemoveShoppingCartIcon color={'disabled'} />
              ) : (
                <ShoppingCartIcon color={'primary'} />
              )}
            </IconButton>
          </Tooltip>
        ]
      }
    );
  }

  if (onCloseUpdate && !disableEdit) {
    columns.push({
      field: 'actions',
      type: 'actions',
      width: 75,
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => [
        <TransportDocumentLineUpdateDialog
          key={980}
          ddtLineId={params.row.id}
          supplierId={params.row.supplier_id}
          supplierCode={params.row.supplier}
          warehouseId={params.row.warehouse_id}
          warehouseCode={params.row.warehouse}
          onClose={onCloseUpdate}
        />
      ],
      filterable: false,
      sortable: false
    } as GridColDef);
  }

  return columns;
}
